import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import Dropzone, { useDropzone } from 'react-dropzone';
import { TextField, Box, FormControl, Select, InputLabel, MenuItem, Typography, Button, Grid } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { IChange } from './Timeline';
import { changeTypes } from '../../../utils/Constants';
import { baseStyle, acceptStyle, activeStyle, rejectStyle } from '../../../css/dropzone';
import { useAppDispatch } from '../../../app/hooks';
import { showErrorSnackbar } from '../../../features/snackbar/snackbarSlice';
import { selectProject } from '../../../features/project/projectSlice';
import { selectUser } from '../../../features/auth/authSlice';
import { FlexEnd } from '../../../commonStyles';
import { IAttachment } from '../../../types/task';
import PdfCard from '../../common/PdfCard';

const TimelineForm = (props: ChildProps) => {
  const { change, handleSubmit, deleteChange, deleteAttachment } = props;
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const storeDispatch = useAppDispatch();
  const [description, setDescription] = useState(change ? change.description : '');
  const [unitsAffected, setUnitsAffected] = useState(change ? change.unitsAffected : '');
  const [changeType, setChangeType] = useState(change ? change.type : '');
  const [dateSubmitted, setDateSubmitted] = useState<Date | null>(change ? change.dateSubmitted : null);
  const [submittedBy, setSubmittedBy] = useState(change ? change.submittedBy : '');
  const [attachments, setAttachments] = useState<any[]>(change ? change.attachments : []);

  const [updateAttachments] = useMutation(UPDATEATTACHMENTS, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err);
    },
  });

  const { isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept:
      'image/jpeg, image/png, application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values',
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const handleDrop = async (acceptedFiles: any) => {
    if (acceptedFiles.length === 0) {
      storeDispatch(showErrorSnackbar('This file type is not allowed'));
      return;
    }
    const files = acceptedFiles;
    let allFiles: any[] = [];
    if (files.length) {
      for (let i = 0; i < files.length; i++) {
        let filePromise = new Promise((resolve) => {
          const fileReader = new FileReader();
          fileReader.readAsDataURL(files[i]);
          fileReader.onloadend = async () =>
            resolve({
              url: fileReader.result,
              name: files[i].name,
              file: files[i],
            });
        });
        allFiles.push(filePromise);
      }
    }
    Promise.all(allFiles).then((fileContents: any) => {
      setAttachments([...attachments, ...fileContents]);
    });
  };

  const handleDelete = (id: number, title: string, numIndex: number) => {
    if (id) {
      deleteAttachment({ variables: { _id: change?._id, attachmentId: id, deleteFile: true } });
      setAttachments([...attachments.filter((attachment: any) => attachment._id !== id)]);
    } else {
      let removedAttachments = attachments.filter((id: any, index: number) => numIndex !== index);
      setAttachments(removedAttachments);
    }
  };

  const submitForm = async (e: any) => {
    e.preventDefault();
    const formData = {
      project: project._id,
      description,
      user: user._id,
      unitsAffected,
      type: changeType,
      dateSubmitted: dateSubmitted ? new Date(dateSubmitted) : null,
      submittedBy,
      attachments,
    };

    if (change) {
      for (const file of attachments) {
        if (file.file) {
          await updateAttachments({ variables: { id: change._id, name: file.file.name, file: file.file } });
        }
      }
      formData.attachments = attachments.map((attachment: IAttachment) => {
        return { name: attachment.name };
      });
      handleSubmit({
        variables: {
          _id: change._id,
          record: formData,
        },
      });
    } else {
      formData.attachments = attachments.map((attachment: IAttachment) => {
        return { name: attachment.name };
      });
      handleSubmit({
        variables: {
          record: formData,
          files: attachments.length ? attachments.map((attachment: any) => attachment.file) : [],
        },
      });
    }
  };

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 2 }}>
        <strong>{change ? 'Edit' : 'Add'} Change</strong>
      </Typography>
      <form onSubmit={submitForm}>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            name={'Description'}
            onChange={(e: any) => setDescription(e.target.value)}
            value={description}
            label={'Description'}
            placeholder={'Enter Text'}
            required={true}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            name={'unitsAffected'}
            onChange={(e: any) => setUnitsAffected(e.target.value)}
            value={unitsAffected}
            label={'Units(s) Area Affected'}
            placeholder={'Enter Text'}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <FormControl required fullWidth>
            <InputLabel id="id-select-label">Change Type</InputLabel>
            <Select
              labelId="id-label"
              id="changeType"
              value={changeType}
              label="Change Type"
              name="changeType"
              onChange={(e) => setChangeType(e.target.value)}
              required={true}
            >
              {changeTypes.map((changeType: string) => {
                return <MenuItem value={changeType}>{changeType}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            name={'submittedBy'}
            onChange={(e: any) => setSubmittedBy(e.target.value)}
            value={submittedBy}
            label={'Submitted By (Developer Name, Project Manager, etc.)'}
            placeholder={'Enter Text'}
            required={true}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              views={['year', 'month', 'day']}
              label={'Submitted Date (YYYY/MM/DD)'}
              value={dateSubmitted ? new Date(dateSubmitted) : null}
              onChange={(newValue) => setDateSubmitted(newValue)}
              format="yyyy/MM/dd"
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
        </Box>
        <Dropzone
          onDrop={(files) => handleDrop(files)}
          accept="image/jpg, image/jpeg, image/png, application/pdf, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values"
        >
          {({ getRootProps, getInputProps }) => (
            <section>
              <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <p>Drag and Drop or Upload PDFs/Images/Excels</p>
              </div>
            </section>
          )}
        </Dropzone>
        {attachments.length > 0 ? (
          <Grid container spacing={2}>
            {attachments.map((attachment: any, index: number) => {
              return (
                <Grid key={index} item lg={3} md={4} sm={6} xs={12}>
                  <PdfCard
                    file={attachment.getUrl ? attachment.getUrl : attachment.url}
                    title={attachment.name ? attachment.name : attachment.file.name}
                    id={attachment._id}
                    handleDelete={handleDelete}
                    download={true}
                    index={index}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : null}
        <FlexEnd>
          {change ? (
            <Button
              onClick={() =>
                deleteChange({
                  variables: {
                    _id: change._id,
                  },
                })
              }
              color="error"
              variant="contained"
              sx={{ mt: 2, mr: 2 }}
            >
              Delete
            </Button>
          ) : null}
          <Button type="submit" color="success" variant="contained" sx={{ mt: 2 }}>
            {change ? 'Update' : 'Submit'}
          </Button>
        </FlexEnd>
      </form>
    </Box>
  );
};

interface ChildProps {
  change: IChange | null;
  handleSubmit: any;
  deleteChange?: any;
  deleteAttachment?: any;
}

const UPDATEATTACHMENTS = gql`
  mutation addChangeAttachments($id: MongoID!, $name: String!, $file: Upload!) {
    addChangeAttachments(id: $id, name: $name, file: $file) {
      _id
      attachments {
        _id
        name
        getUrl
      }
    }
  }
`;

export default TimelineForm;
